import { useEffect } from 'react';
import {
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  LexicalEditor,
  LexicalNode,
  PASTE_COMMAND,
} from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';
import { getAvailableWidth } from '@/components/LexicalEditor/plugins/TableCellResizer';

// pt → px 근사(레거시 TinyMCE cleanHTML 계약: ×1.3333 올림, 최소 1px)
const PT_TO_PX = 1.3333;

// 셀 자체 style 에 얹혀 오는 텍스트 서식.
// @lexical/table 셀 변환은 이 중 font-weight(bold)만 살리고 color/size/decoration 은 버리므로,
// 셀 안 텍스트 노드로 내려 ExtendedTextNode(span importDOM)가 보존하게 재배치한다.
const TEXT_FORMAT_PROPS = [
  'font-weight',
  'font-style',
  'text-decoration',
  'color',
  'font-size',
  'font-family',
];

const BLOCK_TAGS = new Set([
  'p',
  'div',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
]);

const isBlockElement = (node: Element) =>
  BLOCK_TAGS.has(node.tagName.toLowerCase());

const ptToPx = (value: string) =>
  value.replace(
    /(-?\d*\.?\d+)pt/gi,
    (_, n) => `${Math.max(1, Math.ceil(parseFloat(n) * PT_TO_PX))}px`,
  );

// mso-* 제거, windowtext→#000, pt→px, background 단일 색상→background-color, &quot; 정리
const normalizeStyleText = (styleText: string): string =>
  styleText
    .split(';')
    .map((decl) => {
      const idx = decl.indexOf(':');
      if (idx === -1) return '';
      let prop = decl.slice(0, idx).trim().toLowerCase();
      let value = decl.slice(idx + 1).trim();
      if (!prop || !value) return '';
      if (prop.startsWith('mso-')) return '';
      value = value.replace(/windowtext/gi, '#000000');
      value = ptToPx(value);
      // Excel 전용 border-style 'hairline'(가장 얇은 실선)은 표준 CSS 가 아니라,
      // 브라우저가 해당 border 선언을 통째로 무효화 → 가장자리 보더가 통으로 사라진다.
      // 실선(solid)으로 환원해 살린다.
      value = value.replace(/\bhairline\b/gi, 'solid');
      value = value.replace(/&quot;/gi, '"').trim();
      // background 값이 "단일 색상 토큰"일 때만 background-color 로(셀 변환기는 backgroundColor 만 읽음).
      // rgb()/hsl() 는 괄호 안 공백 허용, #fff url(...) repeat 같은 복합 shorthand 는 제외.
      if (
        prop === 'background' &&
        /^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|transparent|[a-z]+)$/i.test(
          value,
        ) &&
        !/^(none|url|linear-gradient|radial-gradient)/i.test(value)
      ) {
        prop = 'background-color';
      }
      return value ? `${prop}: ${value}` : '';
    })
    .filter(Boolean)
    .join('; ');

// 구글 시트: <google-sheets-html-origin> 래퍼 언랩 + data-sheets-* 제거
const stripGoogleSheetsWrapper = (doc: Document) => {
  doc.querySelectorAll('google-sheets-html-origin').forEach((wrapper) => {
    const parent = wrapper.parentNode;
    while (wrapper.firstChild) {
      parent?.insertBefore(wrapper.firstChild, wrapper);
    }
    parent?.removeChild(wrapper);
  });
  doc.querySelectorAll('[data-sheets-value]').forEach((el) => {
    Array.from(el.attributes)
      .filter((attr) => attr.name.startsWith('data-sheets-'))
      .forEach((attr) => el.removeAttribute(attr.name));
  });
};

// Excel 데스크톱: 스타일은 <style> 블록의 .xlNN 클래스에 담겨 오는데
// $generateNodesFromDOM 은 <style> 을 적용하지 않으므로 클래스 규칙을 셀 inline 으로 펼친다.
const expandStyleClasses = (doc: Document) => {
  const styleEls = Array.from(doc.querySelectorAll('style'));
  if (!styleEls.length) return;
  // 떼지 않으면 첫 규칙 셀렉터에 붙어(<!--td) 그 규칙이 통째로 버려짐
  const cssText = styleEls
    .map((el) => el.textContent || '')
    .join('\n')
    .replace(/<!--|-->/g, ' ');
  const rules: Array<{ selector: string; body: string }> = [];
  cssText.replace(
    /([^{}]+)\{([^{}]*)\}/g,
    (_, selector: string, body: string) => {
      rules.push({ selector: selector.trim(), body: body.trim() });
      return '';
    },
  );
  // 엑셀은 복사 범위에서 지배적인 세로정렬을 태그 전역 규칙(td)에만 싣고
  // .xlNN 에는 예외 셀만 담으므로, 전역 규칙을 버리면 표 전체가 Lexical 기본값 top 으로 떨어진다.
  let tagVerticalAlign = '';
  rules.forEach(({ selector, body }) => {
    if (!body) return;
    selector.split(',').forEach((one) => {
      const sel = one.trim();
      if (!sel) return;
      // 클래스 셀렉터만 펼칠 것. 태그 전역 규칙의 나머지(색/폰트/보더/줄바꿈)는
      // 엑셀 기본값이라 계속 무시 — 붙여넣기 round-trip 기본값 계약 유지.
      if (!sel.includes('.')) {
        if (sel === 'td' || sel === 'th') {
          const matched = /(?:^|;)\s*vertical-align\s*:\s*([^;]+)/i.exec(body);
          if (matched) tagVerticalAlign = matched[1].trim();
        }
        return;
      }
      let targets: Element[] = [];
      try {
        targets = Array.from(doc.querySelectorAll(sel));
      } catch {
        return;
      }
      targets.forEach((el) => {
        const existing = el.getAttribute('style') || '';
        // 클래스 규칙을 앞에, 기존 inline 을 뒤에 → CSS 우선순위대로 inline 이 최종 승리.
        el.setAttribute('style', `${body}; ${existing}`);
      });
    });
  });
  if (tagVerticalAlign) {
    doc.querySelectorAll('td, th').forEach((cellEl) => {
      const cell = cellEl as HTMLTableCellElement;
      // 클래스/inline 선언과 valign 속성이 우선 — 전역 규칙은 미지정 셀만 채울 것
      if (cell.style.verticalAlign || cell.getAttribute('valign')) return;
      cell.style.verticalAlign = tagVerticalAlign;
    });
  }
  styleEls.forEach((el) => el.remove());
};

// @lexical/table 표 변환기는 col 요소를 하나씩 세며 span 을 보지 않음.
// 엑셀이 같은 폭 연속 열을 span 으로 묶어 보내면 colWidths 가 열 수보다 짧아져
// 뒤쪽 열은 조절 불가, 앞쪽 열은 폭이 한 칸씩 밀림.
const expandColSpans = (doc: Document) => {
  doc.querySelectorAll('col').forEach((col) => {
    const span = parseInt(col.getAttribute('span') || '1', 10);
    col.removeAttribute('span');
    if (!Number.isFinite(span) || span <= 1) return;
    const parent = col.parentNode;
    if (!parent) return;
    for (let i = 1; i < span; i += 1) {
      const clone = col.cloneNode(true) as Element;
      parent.insertBefore(clone, col.nextSibling);
    }
  });
};

// colgroup 이 열 수보다 짧으면 조절할 수 없는 열이 생기므로 첫 행 셀 width 로 메움.
// 열 수는 전 행 최댓값으로 셀 것 — Lexical 은 import 때 짧은 행을 최대 열 수로 패딩하므로
// TableNode.getColumnCount()(첫 행 기준)도 결국 이 값이 됨. 첫 행만 세면 제목행처럼
// 짧은 첫 행을 가진 표에서 colgroup 이 짧아지고, 정규화가 재생성하며 폭이 버려짐.
const ensureFullColgroup = (doc: Document) => {
  doc.querySelectorAll('table').forEach((table) => {
    const rows = getRows(table);
    if (!rows.length) return;
    const columnCount = rows.reduce(
      (max, row) =>
        Math.max(
          max,
          getRowCells(row).reduce((n, cell) => n + (cell.colSpan || 1), 0),
        ),
      0,
    );
    if (!columnCount) return;

    // 중첩 표의 col 까지 잡히면 안쪽 표 colgroup 을 바깥 표 폭으로 덮어씀
    const existing = Array.from(
      table.querySelectorAll(':scope > colgroup > col'),
    );
    const widths: string[] = [];
    existing.forEach((col) => {
      const styleWidth = (col as HTMLElement).style.width;
      const attrWidth = col.getAttribute('width');
      widths.push(styleWidth || (attrWidth ? `${attrWidth}px` : ''));
    });

    // 엑셀은 첫 행 td 에도 열너비를 실음
    let colIndex = 0;
    getRowCells(rows[0]).forEach((cell) => {
      const span = cell.colSpan || 1;
      const cellWidth = cell.style.width;
      if (cellWidth && span === 1 && !widths[colIndex]) {
        widths[colIndex] = cellWidth;
      }
      colIndex += span;
    });

    let colGroup = table.querySelector(':scope > colgroup');
    if (!colGroup) {
      colGroup = doc.createElement('colgroup');
      table.insertBefore(colGroup, table.firstChild);
    }
    // 길이를 columnCount 로 맞춰서 판정 — slice 는 짧은 배열을 채우지 않고
    // some 은 빈 칸(hole)을 건너뛰므로, 그대로 쓰면 폭이 모자란 colgroup 이 통과함
    const filled = Array.from(
      { length: columnCount },
      (_, i) => widths[i] || '',
    );
    // 부분만 채운 colgroup 은 그대로 읽혀 열이 밀림 — 통째로 버리면
    // 리사이저 정규화가 균등 폭으로 초기화
    if (filled.some((w) => !w)) {
      colGroup.remove();
      return;
    }
    const cols = filled.map((w) => {
      const col = doc.createElement('col');
      col.style.width = w;
      return col;
    });
    colGroup.replaceChildren(...cols);
  });
};

// <col> 너비를 대응 셀로 매핑(Excel 은 열너비를 col 에만 싣는 경우가 많음).
const applyColWidths = (doc: Document) => {
  doc.querySelectorAll('table').forEach((table) => {
    const cols = Array.from(table.querySelectorAll(':scope > colgroup > col'));
    if (!cols.length) return;
    const widths: string[] = [];
    cols.forEach((col) => {
      const styleWidth = (col as HTMLElement).style.width;
      const attrWidth = col.getAttribute('width');
      const width = styleWidth || (attrWidth ? `${attrWidth}px` : '');
      // expandColSpans 앞에 돌 수도 있어 여기서도 span 을 펼쳐 인덱스를 맞춤
      const span = Math.max(
        1,
        parseInt(col.getAttribute('span') || '1', 10) || 1,
      );
      for (let i = 0; i < span; i += 1) widths.push(width);
    });
    // 직속 행만 — 하위 전체를 훑으면 바깥 표 열너비가 중첩 표 셀에 주입되고,
    // 그 값으로 안쪽 colgroup 까지 만들어짐
    getRows(table).forEach((row) => {
      let colIndex = 0;
      getRowCells(row).forEach((cell) => {
        if (!cell.style.width && widths[colIndex]) {
          cell.style.width = widths[colIndex];
        }
        colIndex += cell.colSpan || 1;
      });
    });
  });
};

// "큰 표" = 본문 폭의 이 비율 이상을 차지하는 표.
// 열 수는 기준에서 제외 — 데이터 표는 대개 5열 이상이라 전부 큰 표로 걸림.
const WIDE_TABLE_MIN_RATIO = 0.6;

// 큰 표만 본문 폭에 맞춰 늘림 — 작은 표는 늘리면 셀이 과하게 벌어짐.
// 붙여넣기 시점 1회 판정. 이후 사용자가 조절한 폭은 리사이저 정규화가 보존.
const stretchWideTables = (doc: Document, available: number) => {
  if (available <= 0) return;
  doc.querySelectorAll('table').forEach((table) => {
    const cols = Array.from(
      table.querySelectorAll(':scope > colgroup > col'),
    ) as HTMLElement[];
    if (!cols.length) return;
    const widths = cols.map((col) => parseFloat(col.style.width) || 0);
    // 폭 모르는 열이 있으면 비율을 못 지킴(colgroup 없는 v1 표는 리사이저가 균등 분배)
    if (widths.some((w) => w <= 0)) return;
    const sum = widths.reduce((a, b) => a + b, 0);
    if (sum <= 0 || sum >= available) return;
    if (sum < available * WIDE_TABLE_MIN_RATIO) return;
    const scale = available / sum;
    cols.forEach((col, i) => {
      col.style.width = `${Math.max(1, Math.floor(widths[i] * scale))}px`;
    });
  });
};

// 이 표의 직속 행만(중첩 표의 tr 은 제외)
const getRows = (table: Element): HTMLTableRowElement[] =>
  Array.from(
    table.querySelectorAll(
      ':scope > tr, :scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr',
    ),
  ) as HTMLTableRowElement[];

const getRowCells = (row: Element): HTMLTableCellElement[] =>
  Array.from(
    row.querySelectorAll(':scope > td, :scope > th'),
  ) as HTMLTableCellElement[];

// 셀 "내용 없음": 텍스트(공백/nbsp 제거) 없고 이미지·중첩표 등 미디어도 없음.
// 배경색/테두리만 있는 셀은 빈 것으로 본다(엑셀 빈 격자 칸이 여기 해당).
const isCellEmpty = (cell: Element): boolean => {
  if (cell.querySelector('img, table, svg, video, iframe')) return false;
  return (cell.textContent || '').replace(/\u00a0/g, ' ').trim() === '';
};

// 엑셀은 선택영역/usedRange 여파로 실제 데이터 밖 빈 셀도 <tr><td> 로 실어 보낸다.
// 가장자리(선행/후행)의 "전체가 빈" 행·열만 잘라 실제 데이터 범위만 남긴다.
// 내부 빈 행/열과 일부만 빈 가장자리(교차표 코너 등)는 보존.
const isRowTrimmable = (row: Element): boolean => {
  const cells = getRowCells(row);
  if (!cells.length) return true;
  // 아래로 뻗는 rowspan 셀이 있으면 제거 시 하단 행 정렬이 깨지므로 남긴다.
  return cells.every((cell) => isCellEmpty(cell) && (cell.rowSpan || 1) <= 1);
};

const removeRow = (row: HTMLTableRowElement) => {
  const parent = row.parentElement;
  row.remove();
  if (
    parent &&
    parent.tagName.toLowerCase() !== 'table' &&
    !parent.querySelector('tr')
  ) {
    parent.remove();
  }
};

const trimEmptyRows = (table: Element) => {
  let rows = getRows(table);
  while (rows.length && isRowTrimmable(rows[0])) {
    removeRow(rows[0]);
    rows = getRows(table);
  }
  while (rows.length && isRowTrimmable(rows[rows.length - 1])) {
    removeRow(rows[rows.length - 1]);
    rows = getRows(table);
  }
};

const hasMergedCells = (table: Element): boolean =>
  getRows(table).some((row) =>
    getRowCells(row).some(
      (cell) => (cell.colSpan || 1) > 1 || (cell.rowSpan || 1) > 1,
    ),
  );

// 병합 셀이 있으면 열 인덱스가 불안정하므로 열 트리밍은 스킵(행 트리밍만 적용).
const trimEmptyCols = (table: Element) => {
  if (hasMergedCells(table)) return;
  const rows = getRows(table);
  if (!rows.length) return;

  const colCount = () => Math.max(0, ...rows.map((r) => getRowCells(r).length));
  const isColEmpty = (col: number) =>
    rows.every((row) => {
      const cell = getRowCells(row)[col];
      return !cell || isCellEmpty(cell);
    });
  const removeColAt = (col: number) => {
    rows.forEach((row) => getRowCells(row)[col]?.remove());
    // 대응 <col> 도 제거해 열너비 매핑 유지
    Array.from(table.querySelectorAll(':scope > colgroup > col'))[
      col
    ]?.remove();
  };

  while (colCount() > 0 && isColEmpty(0)) removeColAt(0);
  let last;
  while ((last = colCount() - 1) >= 0 && isColEmpty(last)) removeColAt(last);
};

const trimEmptyEdges = (doc: Document) => {
  doc.querySelectorAll('table').forEach((table) => {
    trimEmptyRows(table);
    trimEmptyCols(table);
    // 전부 트리밍돼 남은 셀이 없으면 표 자체 제거
    if (!table.querySelector('td, th')) table.remove();
  });
};

// 엑셀 소스 줄바꿈(개행+들여쓰기)이 접힌 공백. 개행 없는 연속 공백은 사용자 입력일 수 있어 제외.
const SOURCE_WRAP = /[ \t\r]*[\r\n][ \t\r\n]*/g;

// 줄 경계(직전/직후가 <br> 이거나 셀 끝) 판정 — 그 자리 공백은 브라우저가 버리는 자리
const atLineBoundary = (node: Node, root: Element, prev: boolean): boolean => {
  let cur: Node | null = node;
  while (cur && cur !== root) {
    const sibling: Node | null = prev ? cur.previousSibling : cur.nextSibling;
    if (sibling) {
      return (
        sibling.nodeType === Node.ELEMENT_NODE &&
        (sibling as Element).tagName.toLowerCase() === 'br'
      );
    }
    cur = cur.parentNode;
  }
  return true;
};

// 엑셀은 HTML 을 약 78칼럼에서 접어 내보내고, 회계 표시형식의 채움문자를 NBSP 런으로 실체화한다.
// 에디터·조회가 white-space: pre-wrap 이라 둘 다 접히지 않고 원본에 없던 공백으로 보이므로,
// 브라우저가 접었을 모습으로 미리 정리할 것.
const normalizeCellWhitespace = (doc: Document) => {
  doc.querySelectorAll('td, th').forEach((cellEl) => {
    const cell = cellEl as HTMLTableCellElement;
    const walker = doc.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    while (walker.nextNode()) texts.push(walker.currentNode as Text);

    texts.forEach((node) => {
      const raw = node.data;
      // 채움문자는 런 길이로만 구분 가능 — 사용자가 넣은 단일 NBSP 는 보존
      let next = raw.replace(/\u00a0{2,}/g, ' ');
      if (/[\r\n]/.test(next)) {
        const leading = /^[ \t\r]*[\r\n]/.test(next);
        const trailing = /[\r\n][ \t\r\n]*$/.test(next);
        next = next.replace(SOURCE_WRAP, ' ');
        if (leading && atLineBoundary(node, cell, true)) {
          next = next.replace(/^ /, '');
        }
        if (trailing && atLineBoundary(node, cell, false)) {
          next = next.replace(/ $/, '');
        }
      }
      if (next !== raw) node.data = next;
    });
  });
};

// align/valign 속성 → style(셀 변환기는 style 만 읽음)
const promoteCellAttributes = (doc: Document) => {
  doc.querySelectorAll('td, th').forEach((cellEl) => {
    const cell = cellEl as HTMLTableCellElement;
    const align = cell.getAttribute('align');
    if (align && !cell.style.textAlign) cell.style.textAlign = align;
    const valign = cell.getAttribute('valign');
    if (valign && !cell.style.verticalAlign) cell.style.verticalAlign = valign;
  });
};

const normalizeAllStyles = (doc: Document) => {
  doc.querySelectorAll('[style]').forEach((el) => {
    const normalized = normalizeStyleText(el.getAttribute('style') || '');
    if (normalized) {
      el.setAttribute('style', normalized);
    } else {
      el.removeAttribute('style');
    }
  });
};

// 셀 서식을 Lexical 이 읽는 위치로 재배치:
//   text-align → 셀 변환기가 못 살리므로 내용을 <p style="text-align"> 로 감싸 ParagraphNode.format 으로 보존
//   color/font-size 등 → <span> 으로 감싸 ExtendedTextNode 가 보존
// background-color/width/vertical-align 은 셀 변환기가 직접 살리므로 td 에 남긴다.
const redistributeCellStyles = (doc: Document) => {
  doc.querySelectorAll('td, th').forEach((cellEl) => {
    const cell = cellEl as HTMLElement;
    const align = cell.style.textAlign;
    const textFormat = TEXT_FORMAT_PROPS.map((prop) => {
      const value = cell.style.getPropertyValue(prop);
      return value ? `${prop}: ${value}` : '';
    }).filter(Boolean);

    const hasBlockChild = Array.from(cell.children).some(isBlockElement);

    if (hasBlockChild) {
      // 이미 블록(<p> 등)을 가진 셀(포탈 조회표 등): 정렬만 블록 자식에 전파, 텍스트 서식은 내부 span 이 보유.
      if (align) {
        Array.from(cell.children).forEach((child) => {
          const el = child as HTMLElement;
          if (isBlockElement(child) && !el.style.textAlign) {
            el.style.textAlign = align;
          }
        });
        cell.style.removeProperty('text-align');
      }
      return;
    }

    if (!textFormat.length && !align) return;

    let inner: HTMLElement = doc.createElement('span');
    if (textFormat.length) inner.setAttribute('style', textFormat.join('; '));
    while (cell.firstChild) inner.appendChild(cell.firstChild);

    if (align) {
      const paragraph = doc.createElement('p');
      paragraph.setAttribute('style', `text-align: ${align}; margin: 0`);
      if (textFormat.length) {
        paragraph.appendChild(inner);
      } else {
        // 서식 span 이 불필요하면 내용을 p 로 직접 이동(빈 span 방지)
        while (inner.firstChild) paragraph.appendChild(inner.firstChild);
      }
      inner = paragraph;
    }

    cell.appendChild(inner);
    TEXT_FORMAT_PROPS.forEach((prop) => cell.style.removeProperty(prop));
    cell.style.removeProperty('text-align');
  });
};

// class/id 는 스타일 펼친 뒤 불필요 + Lexical 혼선 방지 위해 제거(style/colspan/rowspan 은 유지)
const stripNoiseAttributes = (doc: Document) => {
  doc.querySelectorAll('[class], [id]').forEach((el) => {
    el.removeAttribute('class');
    el.removeAttribute('id');
  });
  doc.querySelectorAll('table').forEach((table) => {
    ['border', 'cellspacing', 'cellpadding', 'width'].forEach((attr) =>
      table.removeAttribute(attr),
    );
  });
};

const normalizeTableHtml = (html: string, available: number): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  stripGoogleSheetsWrapper(doc);
  expandStyleClasses(doc);
  expandColSpans(doc);
  applyColWidths(doc);
  trimEmptyEdges(doc);
  normalizeCellWhitespace(doc);
  promoteCellAttributes(doc);
  normalizeAllStyles(doc);
  redistributeCellStyles(doc);
  stripNoiseAttributes(doc);
  // 트리밍으로 열 수가 바뀐 뒤에 맞출 것
  ensureFullColgroup(doc);
  stretchWideTables(doc, available);
  return doc.body.innerHTML;
};

/**
 * 표를 붙여넣을 때 스타일(배경색/볼드/정렬/열너비)을 보존하는 PASTE 핸들러.
 * 표가 포함된 HTML 만 가로채 정규화 후 삽입하고, 그 외 붙여넣기는 기본 동작에 위임(false 반환).
 */
export default function usePasteWithTableStyles(editor: LexicalEditor) {
  useEffect(() => {
    return editor.registerCommand<ClipboardEvent>(
      PASTE_COMMAND,
      (event) => {
        const html = event.clipboardData?.getData('text/html');
        if (!html || !/<table[\s>]/i.test(html)) return false;

        event.preventDefault();
        const normalized = normalizeTableHtml(html, getAvailableWidth(editor));
        const doc = new DOMParser().parseFromString(normalized, 'text/html');
        const nodes: LexicalNode[] = $generateNodesFromDOM(editor, doc) || [];
        if (!nodes.length) return true;

        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertNodes(nodes);
          } else {
            $getRoot().selectEnd();
            $insertNodes(nodes);
          }
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);
}
